import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Send, MessageCircle, Sparkles, Baby, User, Loader2, ChevronDown, ChevronUp, FileText, Database, TrendingUp, History, Clock, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import RatingButtons from './RatingButtons';
import RatingMetrics from './RatingMetrics';
import { isExpert, checkUserRating } from '../services/ratingService';
import CategoryBadge from './memory/CategoryBadge';
import MilestoneMatchCard from './memory/MilestoneMatchCard';
import { getSessionMessages, getAllConversationSessions } from '../services/chatService';
import { isMongoObjectIdString } from '../utils/mongoId';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const ChatAssistant = () => {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [babies, setBabies] = useState([]); // Laravel babies from PostgreSQL
  const [selectedChild, setSelectedChild] = useState('general');
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [userRatings, setUserRatings] = useState({}); // Track user's ratings for each message
  const [sessionId, setSessionId] = useState(null); // Track conversation session ID
  const [expandedSources, setExpandedSources] = useState({}); // Track which messages have expanded source details
  const [expandedDocuments, setExpandedDocuments] = useState({}); // Track which documents are expanded per message
  const [expandedMilestones, setExpandedMilestones] = useState({}); // Track which messages have expanded milestone matches
  const [messageCategories, setMessageCategories] = useState({}); // Store category info for messages
  const [pastSessions, setPastSessions] = useState([]); // Store list of past conversation sessions
  const [loadingHistory, setLoadingHistory] = useState(false); // Loading state for history
  const [sidebarOpen, setSidebarOpen] = useState(false); // Sidebar open/closed state (closed by default on mobile, open on desktop)
  const [pendingTrackerAction, setPendingTrackerAction] = useState(null); // Tracker action confirmation
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchChildren();
    fetchBabies();
    initializeChat();
    // Open sidebar by default on desktop (lg breakpoint and above)
    const checkScreenSize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      }
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchChildren = async () => {
    try {
      const response = await axios.get(`${API_BASE}/children`);
      setChildren(response.data);
    } catch (error) {
      console.error('Failed to fetch children:', error);
    }
  };

  const fetchBabies = async () => {
    try {
      const response = await axios.get(`${API_BASE}/chat/babies`);
      setBabies(response.data || []);
    } catch (error) {
      // Non-critical — MongoDB users will get empty list
      console.error('Failed to fetch babies:', error);
    }
  };

  const fetchActiveSession = async () => {
    try {
      const response = await axios.get(`${API_BASE}/chat/session`);
      if (response.data && response.data.exists && response.data.session_id) {
        return response.data.session_id;
      }
    } catch (error) {
      console.error('Failed to fetch active session:', error);
    }
    return null;
  };

  const loadMessagesFromSession = async (sessionId) => {
    if (!sessionId) return;
    
    setLoadingHistory(true);
    try {
      const historyData = await getSessionMessages(sessionId, 100);
      
      if (historyData.messages && historyData.messages.length > 0) {
        // Convert API messages to component format
        const convertedMessages = historyData.messages.map(msg => ({
          id: msg.id || `msg-${Date.now()}-${Math.random()}`,
          type: msg.role === 'user' ? 'user' : 'assistant',
          content: msg.content || '',
          timestamp: msg.created_at ? new Date(msg.created_at) : new Date(),
          metadata: msg.metadata || null
        }));
        
        setMessages(convertedMessages);
      } else {
        // No messages in session, show welcome message
        const welcomeMessage = {
          id: 'welcome',
          type: 'assistant',
          content: `Hello! I'm your Nestology AI assistant 🌱

I'm here to help you understand your child's development and provide expert guidance. You can ask me about:

• Developmental milestones and what they mean
• Age-appropriate activities and play ideas
• Common parenting concerns and solutions
• Understanding your child's behavior
• When to seek professional advice

Feel free to select one of your children from the dropdown above for personalized advice, or ask general questions about child development!`,
          timestamp: new Date()
        };
        setMessages([welcomeMessage]);
      }
    } catch (error) {
      console.error('Failed to load conversation history:', error);
      // On error, show welcome message
      const welcomeMessage = {
        id: 'welcome',
        type: 'assistant',
        content: `Hello! I'm your Nestology AI assistant 🌱

I'm here to help you understand your child's development and provide expert guidance. You can ask me about:

• Developmental milestones and what they mean
• Age-appropriate activities and play ideas
• Common parenting concerns and solutions
• Understanding your child's behavior
• When to seek professional advice

Feel free to select one of your children from the dropdown above for personalized advice, or ask general questions about child development!`,
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadPastSessions = async () => {
    try {
      const sessionsData = await getAllConversationSessions();
      if (sessionsData.sessions) {
        setPastSessions(sessionsData.sessions);
      }
    } catch (error) {
      console.error('Failed to load past sessions:', error);
    }
  };

  const initializeChat = async () => {
    // Try to get active session from backend first
    const backendSessionId = await fetchActiveSession();
    
    // Use backend session if available, otherwise check localStorage
    const storedSessionId = localStorage.getItem('nestology_chat_session_id');
    const sessionToUse = backendSessionId || storedSessionId;
    
    if (sessionToUse) {
      setSessionId(sessionToUse);
      // Update localStorage with backend session if it was different
      if (backendSessionId && backendSessionId !== storedSessionId) {
        localStorage.setItem('nestology_chat_session_id', backendSessionId);
      }
      
      // Load messages from the active session
      await loadMessagesFromSession(sessionToUse);
    } else {
      // No active session, show welcome message
      const welcomeMessage = {
        id: 'welcome',
        type: 'assistant',
        content: `Hello! I'm your Nestology AI assistant 🌱

I'm here to help you understand your child's development and provide expert guidance. You can ask me about:

• Developmental milestones and what they mean
• Age-appropriate activities and play ideas
• Common parenting concerns and solutions
• Understanding your child's behavior
• When to seek professional advice

Feel free to select one of your children from the dropdown above for personalized advice, or ask general questions about child development!`,
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
    
    // Load past sessions list
    await loadPastSessions();
    setIsInitialized(true);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Check if user has already rated a message
  const checkMessageRating = async (messageId) => {
    if (!isExpert(user)) return;
    
    try {
      const rating = await checkUserRating(messageId);
      if (rating) {
        setUserRatings(prev => ({
          ...prev,
          [messageId]: rating
        }));
      }
    } catch (error) {
      console.error('Failed to check message rating:', error);
    }
  };

  // Handle rating submission
  const handleRatingSubmitted = (ratingResponse) => {
    const messageId = ratingResponse.response_id;
    if (!messageId) {
      console.error('No message ID in rating response:', ratingResponse);
      return;
    }
    
    setUserRatings(prev => ({
      ...prev,
      [messageId]: {
        rating: ratingResponse.rating,
        id: ratingResponse.id
      }
    }));
  };

  const startNewConversation = () => {
    const welcomeMessage = {
      id: 'welcome',
      type: 'assistant',
      content: `Hello! I'm your Nestology AI assistant 🌱

I'm here to help you understand your child's development and provide expert guidance. You can ask me about:

• Developmental milestones and what they mean
• Age-appropriate activities and play ideas
• Common parenting concerns and solutions
• Understanding your child's behavior
• When to seek professional advice

Feel free to select one of your children from the dropdown above for personalized advice, or ask general questions about child development!`,
      timestamp: new Date()
    };
    setMessages([welcomeMessage]);
    setSessionId(null);
    localStorage.removeItem('nestology_chat_session_id');
    setUserRatings({});
    setExpandedSources({});
    setExpandedDocuments({});
    setExpandedMilestones({});
    setMessageCategories({});
    setPendingTrackerAction(null);
  };

  const loadSession = async (sessionId) => {
    setSessionId(sessionId);
    localStorage.setItem('nestology_chat_session_id', sessionId);
    await loadMessagesFromSession(sessionId);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');
    setLoading(true);

    // Create assistant message placeholder for streaming
    const tempMessageId = (Date.now() + 1).toString();
    const assistantMessage = {
      id: tempMessageId, // Temporary ID, will be updated with real ID from backend
      type: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
      metadata: null // Will be populated when done event arrives
    };

    setMessages(prev => [...prev, assistantMessage]);

    try {
      // Use streaming endpoint with persistent session ID
      // Backend will validate and manage the session, so we can pass null or existing session_id
      const currentSessionId = sessionId; // Backend will create one if needed
      
      // Build URL - backend will handle session creation if session_id is invalid/missing
      let url = `${API_BASE}/chat/stream?message=${encodeURIComponent(messageToSend)}`;
      if (currentSessionId) {
        url += `&session_id=${encodeURIComponent(currentSessionId)}`;
      }
      
      // Add child_id or baby_id depending on selection
      if (selectedChild && selectedChild !== 'general') {
        if (selectedChild.startsWith('baby-')) {
          const babyId = selectedChild.replace('baby-', '');
          url += `&baby_id=${encodeURIComponent(babyId)}`;
        } else {
          url += `&child_id=${encodeURIComponent(selectedChild)}`;
        }
      }

      // Attach tracker action params if a confirmed write is pending
      if (pendingTrackerRef.current && pendingTrackerRef.current.type) {
        url += `&tracker_action_type=${encodeURIComponent(pendingTrackerRef.current.type)}`;
        if (pendingTrackerRef.current.data) {
          url += `&tracker_action_data=${encodeURIComponent(JSON.stringify(pendingTrackerRef.current.data))}`;
        }
        pendingTrackerRef.current = null; // Clear after attaching
      }

      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 35000); // 35 second timeout (backend has 30s timeout)
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache'
        },
        signal: controller.signal
      });

      if (!response.ok) {
        // Try to parse error detail from response
        let errorDetail = null;
        try {
          const errorData = await response.json();
          errorDetail = errorData.detail || errorData.message || null;
        } catch (e) {
          // If response is not JSON, use status code
          errorDetail = null;
        }
        
        // Create error with detail if available
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.detail = errorDetail;
        throw error;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let gotFallback = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'token') {
                // Update the assistant message with streaming content
                setMessages(prev => prev.map(msg => 
                  msg.id === tempMessageId 
                    ? { ...msg, content: msg.content + data.content }
                    : msg
                ));
              } else if (data.type === 'done') {
                // Get the real message ID and metadata from backend
                const realMessageId = data.message_id;
                const metadata = data.metadata || null;

                // Update the message with real ID, metadata, and mark streaming as complete
                setMessages(prev => prev.map(msg =>
                  msg.id === tempMessageId
                    ? { ...msg, id: realMessageId, isStreaming: false, metadata: metadata }
                    : msg
                ));

                // Sync session_id from backend (validated/created session)
                // Backend returns the validated session_id in metadata
                const backendSessionId = metadata?.session_id || currentSessionId;
                if (backendSessionId) {
                  localStorage.setItem('nestology_chat_session_id', backendSessionId);
                  // Update state with backend session_id (may differ if backend created new session)
                  setSessionId(backendSessionId);
                }

                // Check if user has already rated this message (for experts)
                if (isExpert(user) && isMongoObjectIdString(realMessageId)) {
                  checkMessageRating(realMessageId);
                }

                // Detect tracker logging suggestion from AI (only for baby selections)
                if (selectedChild && selectedChild.startsWith('baby-')) {
                  const trackerSuggestion = metadata?.tracker_suggestion;
                  setMessages(prev => {
                    const msg = prev.find(m => m.id === realMessageId);
                    if (msg && (trackerSuggestion || detectTrackerSuggestion(msg.content))) {
                      setPendingTrackerAction({
                        messageId: realMessageId,
                        ...(trackerSuggestion || {}),
                      });
                    }
                    return prev;
                  });
                }
              } else if (data.type === 'error') {
                // Handle error events. If a fallback already set friendly
                // content, don't overwrite it — the backend often sends
                // fallback followed by error for the same failure.
                if (!gotFallback) {
                  setMessages(prev => prev.map(msg =>
                    msg.id === tempMessageId
                      ? {
                          ...msg,
                          content: `Sorry, I encountered an error: ${data.error}`,
                          isStreaming: false
                        }
                      : msg
                  ));
                }
                toast.error(`AI Error: ${data.error}`);
              } else if (data.type === 'fallback') {
                // Handle fallback events. Backend serializes SSEFallbackEvent
                // as { type, reason, timestamp } — the reason field carries
                // the user-facing message.
                gotFallback = true;
                const fallbackText =
                  data.reason ||
                  data.response ||
                  'Service temporarily unavailable. Please try again.';
                setMessages(prev => prev.map(msg =>
                  msg.id === tempMessageId
                    ? {
                        ...msg,
                        content: fallbackText,
                        isStreaming: false
                      }
                    : msg
                ));
                if (data.message) {
                  toast.info(data.message);
                }
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', parseError);
            }
          }
        }
      }

      // If we reach here and the message is still streaming, mark it as complete
      setMessages(prev => prev.map(msg => 
        msg.id === tempMessageId && msg.isStreaming
          ? { ...msg, isStreaming: false }
          : msg
      ));
      
      // Clear timeout
      clearTimeout(timeoutId);
    } catch (error) {
      console.error('Chat error:', error);
      let errorMessage = 'Sorry, I\'m having trouble responding right now.';
      
      // If error has detail from backend, use it (for 400, 401, 403, etc.)
      if (error.detail) {
        errorMessage = error.detail;
      } else if (error.name === 'AbortError') {
        errorMessage = 'Request timed out. The AI service may be slow to respond. Please try again.';
      } else if (error.status === 400 || error.message.includes('400')) {
        // 400 Bad Request - use backend message if available, otherwise generic
        errorMessage = error.detail || 'Your message could not be processed. Please check your input and try again.';
      } else if (error.status === 404 || error.message.includes('404')) {
        errorMessage = 'It looks like the AI assistant isn\'t configured yet. Please contact an admin to set up OpenRouter integration.';
      } else if (error.status === 500 || error.message.includes('500')) {
        errorMessage = 'The AI service is currently unavailable. Please try again later or contact support.';
      } else if (error.status === 401 || error.message.includes('401')) {
        errorMessage = 'Your session has expired. Please log in again.';
      } else if (error.status === 403 || error.message.includes('403')) {
        errorMessage = 'You don\'t have permission to use the AI assistant.';
      }
      
      // Replace the streaming message with error message
      setMessages(prev => prev.map(msg => 
        msg.id === tempMessageId 
          ? {
              ...msg,
              content: errorMessage,
              isStreaming: false,
              isError: true
            }
          : msg
      ));
      
      // Only show toast for non-user-input errors (not 400 validation errors)
      if (error.status !== 400) {
        toast.error('Failed to get AI response');
      } else {
        // For 400 errors, the message is already shown in the chat, just show a subtle notification
        toast.error(errorMessage, { duration: 3000 });
      }
    } finally {
      setLoading(false);
      // Clear timeout in case it's still running
      if (typeof timeoutId !== 'undefined') {
        clearTimeout(timeoutId);
      }
      // Focus back to input
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getSelectedChildInfo = () => {
    if (!selectedChild || selectedChild === 'general') return null;
    if (selectedChild.startsWith('baby-')) {
      const babyId = parseInt(selectedChild.replace('baby-', ''));
      const baby = babies.find(b => b.id === babyId);
      if (baby) {
        return { ...baby, name: baby.name, birth_date: baby.date_of_birth };
      }
      return null;
    }
    return children.find(child => child.id === selectedChild);
  };

  const calculateAge = (birthDate) => {
    const birth = new Date(birthDate);
    const today = new Date();
    const ageInMonths = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
    
    if (ageInMonths < 12) {
      return `${ageInMonths} months`;
    } else {
      const years = Math.floor(ageInMonths / 12);
      const months = ageInMonths % 12;
      return months > 0 ? `${years}y ${months}m` : `${years} years`;
    }
  };

  // Check if AI response used knowledge base
  const hasKnowledgeContext = (message) => {
    // Use metadata if available (accurate from backend)
    if (message.metadata?.knowledge_base_used) {
      return true;
    }

    // Fallback to heuristic for old messages without metadata
    const content = message.content;
    if (!content) return false;
    const knowledgeIndicators = [
      'based on', 'according to', 'according to the',
      'research shows', 'studies suggest', 'evidence indicates'
    ];
    const lowerContent = content.toLowerCase();
    return knowledgeIndicators.some(indicator => lowerContent.includes(indicator));
  };

  // Check if user has privileged access (admin/expert/superadmin)
  const hasPrivilegedAccess = () => {
    if (!user) return false;
    const role = user.role;
    return role === 'admin' || role === 'expert' || role === 'superadmin';
  };

  // Toggle expanded source details for a message
  const toggleSourceDetails = (messageId) => {
    setExpandedSources(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  // Toggle expanded state for a specific document within a message
  const toggleDocument = (messageId, docIndex) => {
    const key = `${messageId}-${docIndex}`;
    setExpandedDocuments(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Toggle expanded milestone matches for a message
  const toggleMilestones = (messageId) => {
    setExpandedMilestones(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  // Fetch category info for a message (if available)
  useEffect(() => {
    const fetchMessageCategories = async () => {
      // Check if we have message IDs that might have categories
      // This would typically be done after classification completes
      // For now, we'll check message metadata if available
      messages.forEach(async (message) => {
        if (message.type === 'user' && message.id && !messageCategories[message.id]) {
          // In a real implementation, you might fetch category info from the API
          // For now, we'll rely on metadata if it's included in the message
          if (message.metadata?.categories) {
            setMessageCategories(prev => ({
              ...prev,
              [message.id]: message.metadata.categories
            }));
          }
        }
      });
    };
    fetchMessageCategories();
  }, [messages]);

  // Detect if AI response suggests logging to tracker
  const detectTrackerSuggestion = (content) => {
    if (!content) return false;
    const patterns = [
      /would you like me to log that/i,
      /shall I (?:log|record|track) that/i,
      /want me to (?:add|log|record) (?:that|this|it) (?:to|in) your tracker/i,
      /would you like me to (?:add|record|track) (?:that|this|it)/i,
      /I can log that (?:for you|in your tracker)/i,
    ];
    return patterns.some(p => p.test(content));
  };

  // Handle tracker action confirmation — send structured action via streaming endpoint
  const pendingTrackerRef = useRef(null);

  const handleConfirmTrackerAction = async () => {
    if (!pendingTrackerAction) return;
    // Store structured tracker data for the next request
    pendingTrackerRef.current = {
      type: pendingTrackerAction.type || null,
      data: pendingTrackerAction.data || null,
    };
    setPendingTrackerAction(null);

    // Send confirmation as a regular message — tracker params will be added to the URL
    setInputMessage('Yes, please log it.');
    setTimeout(() => {
      const form = document.querySelector('[data-testid="chat-form"]');
      if (form) form.requestSubmit();
    }, 50);
  };

  const suggestedQuestions = [
    "What are normal milestones for a 2-year-old?",
    "How can I encourage my child's language development?",
    "When should I be concerned about my child's behavior?",
    "What activities are good for motor skill development?",
    "How much sleep does my child need at this age?"
  ];

  const formatSessionDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div className="h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex flex-col overflow-hidden">
      <div className="flex flex-1 gap-4 p-4 lg:p-6 max-w-7xl mx-auto w-full overflow-hidden">
        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Header - Compact */}
          <div className="flex-shrink-0 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex-1"></div>
              <div className="flex-1 text-center">
                <h1 className="text-2xl lg:text-3xl font-bold font-display">
                  <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                    AI Assistant
                  </span>{' '}
                  <span>🤖</span>
                </h1>
              </div>
              <div className="flex-1 flex justify-end">
                {pastSessions.length > 0 && (
                  <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="inline-flex items-center px-2 py-1.5 bg-white border border-slate-300 rounded-lg text-xs lg:text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm"
                    aria-label="Toggle sidebar"
                  >
                    <History className="w-4 h-4 mr-1 lg:mr-2" />
                    <span className="hidden sm:inline">Conversations</span>
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {messages.length > 0 && (
                <button
                  onClick={startNewConversation}
                  className="inline-flex items-center px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors shadow-sm"
                >
                  <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  New Conversation
                </button>
              )}
            </div>
          </div>

          {/* Child Selection - Compact */}
          {(children.length > 0 || babies.length > 0) && (
            <div className="flex-shrink-0 mb-3">
              <Card className="glass border-0">
                <CardContent className="p-2 lg:p-3">
                  <div className="flex items-center space-x-2 lg:space-x-4 flex-wrap gap-2">
                    <div className="flex items-center space-x-2">
                      <Baby className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-600" />
                      <span className="font-medium text-slate-700 text-sm">Ask about:</span>
                    </div>
                    <Select value={selectedChild} onValueChange={setSelectedChild} data-testid="child-select">
                      <SelectTrigger className="w-48 lg:w-64 h-8 lg:h-10 text-sm">
                        <SelectValue placeholder="General questions" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General child development</SelectItem>
                        {children.map((child) => (
                          <SelectItem key={child.id} value={child.id}>
                            {child.name} ({calculateAge(child.birth_date)})
                          </SelectItem>
                        ))}
                        {babies.map((baby) => (
                          <SelectItem key={`baby-${baby.id}`} value={`baby-${baby.id}`}>
                            {baby.name} ({baby.age_months != null ? `${baby.age_months} months` : 'age unknown'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {getSelectedChildInfo() && (
                      <div className="flex items-center space-x-2 text-xs text-slate-600">
                        <span>Selected: {getSelectedChildInfo().name}</span>
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                          {selectedChild.startsWith('baby-') && getSelectedChildInfo().age_months != null
                            ? `${getSelectedChildInfo().age_months} months`
                            : getSelectedChildInfo().birth_date
                              ? calculateAge(getSelectedChildInfo().birth_date)
                              : 'age unknown'}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Chat Container - Flex to fill remaining space */}
          <Card className="glass border-0 flex-1 flex flex-col overflow-hidden">
            <CardHeader className="pb-2 pt-3 px-4 flex-shrink-0">
              <CardTitle className="flex items-center space-x-2 font-display text-lg lg:text-xl text-slate-800">
                <Sparkles className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-600" />
                <span>Chat with Nestology AI</span>
              </CardTitle>
              {getSelectedChildInfo() && (
                <CardDescription className="text-xs lg:text-sm">
                  Getting personalized advice for {getSelectedChildInfo().name}
                </CardDescription>
              )}
            </CardHeader>
            
            <CardContent className="p-0 flex-1 flex flex-col overflow-hidden">
              {/* Messages - Scrollable area */}
              <div 
                className="flex-1 overflow-y-auto p-3 lg:p-4 space-y-3 lg:space-y-4 bg-slate-50/50"
                data-testid="chat-messages"
              >
              {loadingHistory && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-600 mr-2" />
                  <span className="text-sm text-slate-600">Loading conversation history...</span>
                </div>
              )}
              {!loadingHistory && messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start space-x-3 ${
                    message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                  }`}
                  data-testid={`message-${message.type}-${message.id}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.type === 'user' 
                      ? 'bg-gradient-to-br from-emerald-500 to-teal-500' 
                      : message.isError 
                        ? 'bg-red-100'
                        : 'bg-gradient-to-br from-slate-200 to-slate-300'
                  }`}>
                    {message.type === 'user' ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Sparkles className={`w-4 h-4 ${message.isError ? 'text-red-500' : 'text-slate-600'}`} />
                    )}
                  </div>
                  
                  <div className={`chat-bubble ${message.type} ${
                    message.isError ? 'bg-red-50 border border-red-200 text-red-700' : ''
                  }`}>
                    <div className="p-4">
                      {/* Category Badge for User Messages */}
                      {message.type === 'user' && messageCategories[message.id] && (
                        <div className="mb-2 flex items-center gap-2 flex-wrap">
                          <CategoryBadge 
                            domain={messageCategories[message.id].primary} 
                            severity={messageCategories[message.id].severity}
                          />
                          {messageCategories[message.id].importance_score > 0 && (
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <TrendingUp className="h-3 w-3" />
                              {(messageCategories[message.id].importance_score * 100).toFixed(0)}%
                            </div>
                          )}
                        </div>
                      )}
                      
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {(message.content || '').replace(/<!--TRACK:.*?-->/s, '').trimEnd()}
                        {message.isStreaming && (
                          <span className="inline-block w-2 h-4 bg-emerald-500 ml-1 animate-pulse"></span>
                        )}
                      </p>

                      {/* Milestone Matches for User Messages */}
                      {message.type === 'user' && messageCategories[message.id]?.matched_milestones?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <button
                            onClick={() => toggleMilestones(message.id)}
                            className="text-xs text-emerald-600 hover:text-emerald-700 hover:underline flex items-center gap-1 mb-2"
                          >
                            {expandedMilestones[message.id] ? (
                              <>
                                <ChevronUp className="h-3 w-3" />
                                Hide Milestone Matches ({messageCategories[message.id].matched_milestones.length})
                              </>
                            ) : (
                              <>
                                <ChevronDown className="h-3 w-3" />
                                View Milestone Matches ({messageCategories[message.id].matched_milestones.length})
                              </>
                            )}
                          </button>
                          {expandedMilestones[message.id] && (
                            <div className="space-y-2">
                              {messageCategories[message.id].matched_milestones.map((milestone, idx) => (
                                <MilestoneMatchCard key={idx} milestone={milestone} />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {message.type === 'assistant' && !message.isStreaming && !message.isError && hasKnowledgeContext(message) && (
                        <div className="mt-2 space-y-2">
                          <div className="inline-flex items-center px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {(() => {
                              // Determine badge text based on actual search method used
                              const searchMethod = message.metadata?.search_method || 'keyword';
                              if (searchMethod === 'semantic') {
                                return 'Grounded in knowledge base (semantic)';
                              } else if (searchMethod === 'hybrid') {
                                return 'Grounded in knowledge base (hybrid)';
                              } else {
                                return 'Grounded in knowledge base (keyword)';
                              }
                            })()}
                          </div>
                          
                          {/* Source details for privileged users */}
                          {hasPrivilegedAccess() && message.metadata && (
                            <div className="mt-2">
                              <button
                                onClick={() => toggleSourceDetails(message.id)}
                                className="inline-flex items-center text-xs text-emerald-600 hover:text-emerald-700 hover:underline transition-colors"
                              >
                                {expandedSources[message.id] ? (
                                  <>
                                    <ChevronUp className="w-3 h-3 mr-1" />
                                    Hide source details
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="w-3 h-3 mr-1" />
                                    Show source details
                                  </>
                                )}
                              </button>
                              
                              {expandedSources[message.id] && (
                                <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
                                  {/* Knowledge Sources List */}
                                  {message.metadata.knowledge_sources && message.metadata.knowledge_sources.length > 0 && (
                                    <div>
                                      <div className="flex items-center space-x-2 mb-2">
                                        <FileText className="w-3.5 h-3.5 text-emerald-600" />
                                        <span className="text-xs font-semibold text-slate-700">Source Documents:</span>
                                      </div>
                                      <ul className="ml-5 space-y-1">
                                        {message.metadata.knowledge_sources.map((source, idx) => (
                                          <li key={idx} className="text-xs text-slate-600 list-disc">
                                            {source}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  
                                  {/* Detailed Knowledge Base Information - Expandable per document */}
                                  {message.metadata.knowledge_base_details && message.metadata.knowledge_base_details.length > 0 && (
                                    <div className="space-y-2">
                                      <div className="text-xs font-semibold text-slate-700 mb-2">Document Details:</div>
                                      {message.metadata.knowledge_base_details.map((doc, idx) => {
                                        const docKey = `${message.id}-${idx}`;
                                        const isExpanded = expandedDocuments[docKey];
                                        return (
                                          <div key={idx} className="border border-slate-300 rounded-md overflow-hidden">
                                            {/* Document Header - Always Visible */}
                                            <button
                                              onClick={() => toggleDocument(message.id, idx)}
                                              className="w-full px-3 py-2 bg-slate-100 hover:bg-slate-200 transition-colors flex items-center justify-between text-left"
                                            >
                                              <div className="flex items-center space-x-2 flex-1">
                                                <FileText className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                                <span className="text-xs font-medium text-slate-700">{doc.title || `Document ${idx + 1}`}</span>
                                              </div>
                                              {isExpanded ? (
                                                <ChevronUp className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                              ) : (
                                                <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                              )}
                                            </button>
                                            
                                            {/* Document Details - Expandable */}
                                            {isExpanded && (
                                              <div className="px-3 py-2 bg-white space-y-2 text-xs">
                                                {/* Search Method */}
                                                <div className="flex items-center space-x-2">
                                                  <span className="font-semibold text-slate-700">Search Method:</span>
                                                  <span className={`px-2 py-0.5 rounded ${
                                                    doc.search_method === 'semantic'
                                                      ? 'bg-purple-100 text-purple-700'
                                                      : 'bg-amber-100 text-amber-700'
                                                  }`}>
                                                    {doc.search_method === 'semantic' ? 'Semantic (Vector)' : 'Keyword'}
                                                  </span>
                                                </div>
                                                
                                                {/* Relevance Scores */}
                                                <div className="space-y-1">
                                                  <div className="font-semibold text-slate-700 mb-1">Relevance Scores:</div>
                                                  {doc.vector_score > 0 && (
                                                    <div className="flex items-center justify-between pl-2">
                                                      <span className="text-slate-600">Vector Score:</span>
                                                      <span className="font-mono text-emerald-600">{doc.vector_score.toFixed(3)}</span>
                                                    </div>
                                                  )}
                                                  {doc.keyword_score > 0 && (
                                                    <div className="flex items-center justify-between pl-2">
                                                      <span className="text-slate-600">Keyword Score:</span>
                                                      <span className="font-mono text-emerald-600">{doc.keyword_score.toFixed(2)}</span>
                                                    </div>
                                                  )}
                                                  {doc.combined_score > 0 && (
                                                    <div className="flex items-center justify-between pl-2 border-t border-slate-200 pt-1 mt-1">
                                                      <span className="text-slate-700 font-medium">Combined Score:</span>
                                                      <span className="font-mono font-semibold text-emerald-700">{doc.combined_score.toFixed(3)}</span>
                                                    </div>
                                                  )}
                                                </div>
                                                
                                                {/* Document ID */}
                                                {doc.document_id && (
                                                  <div className="pt-1 border-t border-slate-200">
                                                    <div className="flex items-center space-x-2">
                                                      <span className="font-semibold text-slate-700">Document ID:</span>
                                                      <span className="font-mono text-slate-600 text-[10px] break-all">{doc.document_id}</span>
                                                    </div>
                                                  </div>
                                                )}
                                                
                                                {/* Full Chunk Content */}
                                                {doc.chunk && (
                                                  <div className="pt-1 border-t border-slate-200">
                                                    <div className="font-semibold text-slate-700 mb-1">Content Excerpt:</div>
                                                    <div className="pl-2 pr-2 py-2 bg-slate-50 rounded border border-slate-200 max-h-32 overflow-y-auto">
                                                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{doc.chunk}</p>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  
                                  {/* Mem0 Usage */}
                                  {message.metadata.mem0_used !== undefined && (
                                    <div className="pt-2 border-t border-slate-200">
                                      <div className="flex items-center space-x-2">
                                        <Database className="w-3.5 h-3.5 text-blue-600" />
                                        <span className="text-xs font-semibold text-slate-700">Memory Context:</span>
                                        <span className={`text-xs px-2 py-0.5 rounded ${
                                          message.metadata.mem0_used 
                                            ? 'bg-blue-100 text-blue-700' 
                                            : 'bg-slate-100 text-slate-500'
                                        }`}>
                                          {message.metadata.mem0_used ? 'Used' : 'Not Used'}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Additional metadata if available */}
                                  {message.metadata.knowledge_base_used !== undefined && (
                                    <div className="text-xs text-slate-500 pt-1 border-t border-slate-200">
                                      Knowledge base retrieval: {message.metadata.knowledge_base_used ? 'Yes' : 'No'}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="mt-2 text-xs opacity-70">
                        {formatTime(message.timestamp)}
                        {message.isStreaming && (
                          <span className="ml-2 text-emerald-600">Streaming...</span>
                        )}
                      </div>
                      
                      {/* Tracker Action Confirmation */}
                      {message.type === 'assistant' && !message.isStreaming && pendingTrackerAction?.messageId === message.id && (
                        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <p className="text-sm text-emerald-700 font-medium mb-2">
                            Log this to your tracker?
                          </p>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="gradient-emerald text-white h-7 text-xs"
                              onClick={handleConfirmTrackerAction}
                            >
                              Yes, log it
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => setPendingTrackerAction(null)}
                            >
                              No thanks
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Rating Components - Only for assistant messages (not welcome message) */}
                      {message.type === 'assistant' && !message.isStreaming && !message.isError && message.id !== 'welcome' && isMongoObjectIdString(message.id) && (
                        <div className="mt-3">
                          {/* Rating Buttons - Only for experts */}
                          {isExpert(user) && (
                            <RatingButtons
                              messageId={message.id}
                              onRatingSubmitted={handleRatingSubmitted}
                              isRated={!!userRatings[message.id]}
                              userRating={userRatings[message.id]?.rating}
                            />
                          )}
                          
                          {/* Rating Metrics - Show only for expert users */}
                          {isExpert(user) && (
                            <RatingMetrics
                              messageId={message.id}
                              isExpert={isExpert(user)}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {loading && !messages.some(msg => msg.isStreaming) && (
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-slate-600" />
                  </div>
                  <div className="chat-bubble assistant">
                    <div className="p-4 flex items-center space-x-2">
                      <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                      <span className="text-sm text-slate-600">Connecting...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

              {/* Suggested Questions */}
              {messages.length <= 1 && !loading && (
                <div className="flex-shrink-0 p-2 lg:p-3 border-t border-slate-200 bg-white/50">
                  <p className="text-xs lg:text-sm text-slate-600 mb-2">Try asking:</p>
                  <div className="flex flex-wrap gap-1.5 lg:gap-2">
                    {suggestedQuestions.map((question, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        className="text-xs btn-hover h-7 lg:h-8"
                        onClick={() => setInputMessage(question)}
                        data-testid={`suggested-question-${index}`}
                      >
                        {question}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input - Fixed at bottom */}
              <div className="flex-shrink-0 p-2 lg:p-3 border-t border-slate-200 bg-white/70">
                <form onSubmit={handleSendMessage} className="flex space-x-2" data-testid="chat-form">
                  <Input
                    ref={inputRef}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder={selectedChild ? `Ask about ${getSelectedChildInfo()?.name}...` : "Ask about child development..."}
                    className="form-input flex-1 h-9 lg:h-10 text-sm"
                    disabled={loading}
                    data-testid="chat-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={loading || !inputMessage.trim()}
                    className="btn-hover gradient-emerald text-white px-4 lg:px-6 h-9 lg:h-10"
                    data-testid="send-button"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </form>
                <p className="text-[10px] lg:text-xs text-slate-500 mt-1">
                  Press Enter to send. AI responses should not replace professional medical advice.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Past Conversations */}
        {pastSessions.length > 0 && (
          <>
            {/* Mobile Overlay */}
            {sidebarOpen && (
              <div 
                className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                onClick={() => setSidebarOpen(false)}
              />
            )}
            
            {/* Sidebar */}
            <div className={`
              fixed lg:static top-0 right-0 h-screen lg:h-full
              w-72 lg:w-80 bg-white border-l border-slate-200 shadow-xl lg:shadow-none
              z-50 lg:z-auto
              transform transition-transform duration-300 ease-in-out
              ${sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
              flex flex-col
              lg:flex-shrink-0
            `}>
              {/* Sidebar Header */}
              <div className="flex-shrink-0 p-3 lg:p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                <div className="flex items-center gap-2">
                  <History className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-600" />
                  <h2 className="font-semibold text-slate-800 text-sm lg:text-base">Past Conversations</h2>
                  <span className="text-xs text-slate-500 bg-slate-200 px-1.5 lg:px-2 py-0.5 rounded-full">
                    {pastSessions.length}
                  </span>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden p-1 hover:bg-slate-200 rounded transition-colors"
                  aria-label="Close sidebar"
                >
                  <X className="w-4 h-4 lg:w-5 lg:h-5 text-slate-600" />
                </button>
              </div>

              {/* Sidebar Content */}
              <div className="flex-1 overflow-y-auto p-2 lg:p-4 space-y-2">
                {/* New Conversation Button */}
                <button
                  onClick={() => {
                    startNewConversation();
                    setSidebarOpen(false);
                  }}
                  className={`w-full p-2 lg:p-3 rounded-lg border-2 transition-all text-left ${
                    !sessionId 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 lg:w-4 lg:h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 text-xs lg:text-sm">New Conversation</p>
                      <p className="text-[10px] lg:text-xs text-slate-500">Start a fresh chat</p>
                    </div>
                  </div>
                </button>

                {/* Past Sessions List */}
                {pastSessions.map((session) => {
                  const isActive = session.session_id === sessionId;
                  const dateStr = formatSessionDate(session.last_activity_at);
                  
                  return (
                    <button
                      key={session.session_id}
                      onClick={() => {
                        loadSession(session.session_id);
                        setSidebarOpen(false);
                      }}
                      className={`w-full p-2 lg:p-3 rounded-lg border-2 transition-all text-left ${
                        isActive
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start gap-2 lg:gap-3">
                        <div className={`w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          session.is_active 
                            ? 'bg-emerald-100 text-emerald-600' 
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          <MessageCircle className="w-4 h-4 lg:w-5 lg:h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 lg:gap-2 mb-0.5 lg:mb-1">
                            <p className={`font-medium text-xs lg:text-sm truncate ${
                              isActive ? 'text-emerald-700' : 'text-slate-800'
                            }`}>
                              {session.is_active ? 'Active' : 'Past'} Conversation
                            </p>
                            {session.is_active && (
                              <span className="w-1.5 h-1.5 lg:w-2 lg:h-2 bg-emerald-500 rounded-full animate-pulse flex-shrink-0"></span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 lg:gap-2 text-[10px] lg:text-xs text-slate-500 mb-0.5 lg:mb-1">
                            <span>{session.message_count} messages</span>
                            {dateStr && (
                              <>
                                <span>•</span>
                                <span>{dateStr}</span>
                              </>
                            )}
                          </div>
                          {session.child_id && (
                            <div className="text-[10px] lg:text-xs text-emerald-600 mt-0.5 lg:mt-1">
                              Child-specific
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatAssistant;
